import { Component, computed, inject, resource, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import {
  CustomProperty,
  deleteProperty,
  getCustomProperties,
  removeParamFromAllContentControlTags,
  writePropertyTyped
} from '../../../lib/word-content-controls';
import { HomeService } from '../../home/home.service';
import { PropertyGroupList } from '../../shared/property-group-list/property-group-list';
import {
  CONST_PREFIX,
  ORDER_SUFFIX,
  SELECTOR_PREFIX,
  WIZARD_PREFIX
} from '../../shared/wizard-property.constants';

const PARAMETER_PREFIXES = [WIZARD_PREFIX, SELECTOR_PREFIX, CONST_PREFIX];

/** True if the raw property key belongs to a parameter rather than a foreign document property. */
function isParameterProperty(originalKey: string): boolean {
  return PARAMETER_PREFIXES.some((prefix) => originalKey.startsWith(prefix));
}

@Component({
  selector: 'cmi-document-properties',
  imports: [LoadingSpinner, Notification, PropertyGroupList, TranslatePipe],
  templateUrl: './document-properties.html'
})
export class DocumentProperties {
  private readonly _homeService = inject(HomeService);
  private readonly _router = inject(Router);
  private readonly _loadTrigger = signal(0);
  public readonly deletingGroupName = signal<string | null>(null);
  private readonly _customOrder = signal<string[]>([]);
  protected readonly orderError = signal<boolean>(false);

  private readonly _propertiesResource = resource<DisplayProperty[], number>({
    params: () => this._loadTrigger(),
    loader: async (): Promise<DisplayProperty[]> => {
      const properties = await getCustomProperties();
      const rootType = this._homeService.rootType();

      // Load saved sort order from per-group Wizard_*.Order properties
      const orderMap = new Map<string, number>();
      for (const p of properties) {
        if (
          p.key.startsWith(WIZARD_PREFIX) &&
          p.key.endsWith(ORDER_SUFFIX) &&
          p.value !== undefined
        ) {
          const groupName = p.key.slice(WIZARD_PREFIX.length, -ORDER_SUFFIX.length);
          orderMap.set(groupName, Number(p.value));
        }
      }
      if (orderMap.size > 0) {
        const sorted = Array.from(orderMap.entries())
          .sort((a, b) => a[1] - b[1])
          .map(([name]) => name);
        this._customOrder.set(sorted);
      }

      // Show only parameters. Foreign document properties (officeatwork, SharePoint, ...) are
      // not ours to display or edit. Wizard_*.Order is internal bookkeeping, not a parameter.
      return properties
        .filter((p) => isParameterProperty(p.key))
        .filter((p) => !(p.key.startsWith(WIZARD_PREFIX) && p.key.endsWith(ORDER_SUFFIX)))
        .map((property) => {
          let parameterType: string | null = null;

          // Strip Wizard_, Selector_ or Const_ prefix if present
          const keyWithoutPrefix = property.key.startsWith(WIZARD_PREFIX)
            ? property.key.substring(WIZARD_PREFIX.length)
            : property.key.startsWith(SELECTOR_PREFIX)
              ? property.key.substring(SELECTOR_PREFIX.length)
              : property.key.startsWith(CONST_PREFIX)
                ? property.key.substring(CONST_PREFIX.length)
                : property.key;

          if (rootType) {
            // Search through all content providers for a matching parameter
            for (const provider of rootType.contentProvider ?? []) {
              const parameter = (provider.parameters ?? []).find(
                (p) => p.name === keyWithoutPrefix
              );
              if (parameter) {
                parameterType = parameter.typeName;
                break;
              }
            }
          }

          return {
            ...property,
            key: keyWithoutPrefix,
            originalKey: property.key,
            type: parameterType ?? property.type
          };
        });
    }
  });

  public readonly properties = computed(() => this._propertiesResource.value() ?? []);
  public readonly isLoading = computed(() => this._propertiesResource.isLoading());
  public readonly error = computed(() => this._propertiesResource.error());

  private readonly _allGroups = computed((): PropertyGroup[] => {
    const groups = new Map<string, DisplayProperty[]>();
    for (const property of this.properties()) {
      const groupName = property.key.split('.')[0];
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(property);
    }
    return Array.from(groups.entries()).map(([name, props]) => {
      const baseProperty = props.find((p) => p.key === name);
      const type = baseProperty?.type ?? props[0].type;
      const isWizard = props.some((p) => p.originalKey.startsWith(WIZARD_PREFIX));
      return { name, type, isWizard, properties: props };
    });
  });

  public readonly groupedProperties = computed((): PropertyGroup[] => {
    const wizardGroups = this._allGroups().filter((g) => g.isWizard);
    const order = this._customOrder();
    if (order.length === 0) {
      return wizardGroups;
    }
    const sorted: PropertyGroup[] = [];
    for (const name of order) {
      const group = wizardGroups.find((g) => g.name === name);
      if (group) {
        sorted.push(group);
      }
    }
    for (const group of wizardGroups) {
      if (!sorted.some((g) => g.name === group.name)) {
        sorted.push(group);
      }
    }
    return sorted;
  });

  public readonly plainGroups = computed((): PropertyGroup[] => {
    return this._allGroups().filter((g) => !g.isWizard);
  });

  private async _persistOrder(newOrder: string[]): Promise<void> {
    await Promise.all(
      newOrder.map((name, index) =>
        writePropertyTyped(`${WIZARD_PREFIX}${name}${ORDER_SUFFIX}`, index)
      )
    );
  }

  public async onReorder(newOrder: string[]): Promise<void> {
    const previousOrder = this._customOrder();
    this._customOrder.set(newOrder);
    this.orderError.set(false);
    try {
      await this._persistOrder(newOrder);
    } catch (error) {
      console.error('Failed to persist order:', error);
      this._customOrder.set(previousOrder);
      this.orderError.set(true);
    }
  }

  public async deleteGroup(groupName: string): Promise<void> {
    try {
      const group = this._allGroups().find((g) => g.name === groupName);
      if (group) {
        await Promise.all(group.properties.map((p) => deleteProperty(p.originalKey)));
        await removeParamFromAllContentControlTags(groupName);
      }
      // Remove from custom order, delete its .Order property, and re-index remaining
      const newOrder = this._customOrder().filter((n) => n !== groupName);
      this._customOrder.set(newOrder);
      await deleteProperty(`${WIZARD_PREFIX}${groupName}${ORDER_SUFFIX}`);
      await this._persistOrder(newOrder);
      this.deletingGroupName.set(null);
      this._loadTrigger.set(this._loadTrigger() + 1);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  }

  public confirmDelete(groupName: string): void {
    this.deletingGroupName.set(groupName);
  }

  public cancelDelete(): void {
    this.deletingGroupName.set(null);
  }

  public navigateToEdit(groupName: string): void {
    void this._router.navigate(['/settings/document-properties', groupName]);
  }
}

export interface DisplayProperty extends CustomProperty {
  originalKey: string;
}

export interface PropertyGroup {
  name: string;
  type: string;
  isWizard: boolean;
  properties: DisplayProperty[];
}
